import { Host, Slider, Switch } from '@expo/ui';
import { XIcon } from 'lucide-react-native';
import { usePathname } from 'expo-router';
import * as React from 'react';
import {
  Animated,
  Modal,
  PanResponder,
  Pressable,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { DateTimeField } from '@/components/events/date-time-field';
import { devClockStore, type DevClockStore, useClockState, useCurrentTime } from '@/lib/dev-clock';
import {
  devReleaseFeatureStore,
  type DevReleaseFeatureStore,
  type DevReleaseFeatureValues,
  useDevReleaseFeatureState,
} from '@/lib/dev-release-features';
import {
  RELEASE_FEATURE_FLAG_MANIFEST,
  RELEASE_FEATURE_FLAGS,
  type ReleaseFeature,
} from '@/lib/features';
import { BRAND } from '@sixam/config/brand';
import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@sixam/ui/card';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const SPEED_PRESETS = [1, 10, 25, 50, 100] as const;

export type DevTimeControlsProps = Readonly<{
  now: Date;
  onJumpBy: (durationMs: number) => void;
  onReset: () => void;
  onSetSpeed: (speed: number) => void;
  onSetTime: (time: Date) => void;
  speed: number;
}>;

export function DevTimeControls({
  now,
  onJumpBy,
  onReset,
  onSetSpeed,
  onSetTime,
  speed,
}: DevTimeControlsProps) {
  return (
    <View className="gap-5">
      <View className="gap-1">
        <Text variant="mono" selectable>
          {now.toLocaleString()}
        </Text>
        <Text variant="muted">Simulation keeps moving while this panel is closed.</Text>
      </View>

      <DateTimeField label="Simulated time" onChange={onSetTime} value={now} />

      <View className="flex-row flex-wrap gap-2">
        {[
          ['−1 day', -DAY_MS],
          ['−1 hour', -HOUR_MS],
          ['+1 hour', HOUR_MS],
          ['+1 day', DAY_MS],
        ].map(([label, duration]) => (
          <Button
            key={label}
            onPress={() => onJumpBy(duration as number)}
            size="sm"
            variant="outline"
          >
            <Text>{label}</Text>
          </Button>
        ))}
      </View>

      <View className="gap-3">
        <View className="flex-row items-center justify-between">
          <Text variant="large">Speed</Text>
          <Text variant="mono">{speed}×</Text>
        </View>
        <Host
          matchContents={{ vertical: true }}
          seedColor={BRAND.color.orange}
          style={{ minHeight: 44, width: '100%' }}
        >
          <Slider max={100} min={1} onValueChange={onSetSpeed} step={1} value={speed} />
        </Host>
        <View className="flex-row flex-wrap gap-2">
          {SPEED_PRESETS.map((preset) => (
            <Button
              key={preset}
              onPress={() => onSetSpeed(preset)}
              size="sm"
              variant={preset === speed ? 'default' : 'outline'}
            >
              <Text>{preset}×</Text>
            </Button>
          ))}
        </View>
      </View>

      <Button onPress={onReset} variant="secondary">
        <Text>Reset to real time</Text>
      </Button>
    </View>
  );
}

export type DevReleaseFeatureControlsProps = Readonly<{
  hydrated: boolean;
  onSetAll: (enabled: boolean) => void;
  onSetFeature: (feature: ReleaseFeature, enabled: boolean) => void;
  values: DevReleaseFeatureValues;
}>;

export function DevReleaseFeatureControls({
  hydrated,
  onSetAll,
  onSetFeature,
  values,
}: DevReleaseFeatureControlsProps) {
  const enabledCount = RELEASE_FEATURE_FLAG_MANIFEST.filter(([feature]) => values[feature]).length;

  return (
    <View className="gap-4">
      <View className="gap-1">
        <View className="flex-row items-center justify-between gap-3">
          <Text variant="h3">Release features</Text>
          <Text variant="mono">
            {enabledCount}/{RELEASE_FEATURE_FLAG_MANIFEST.length} ON
          </Text>
        </View>
        <Text variant="muted">
          Development starts with every release feature off. These device-only choices override
          PostHog until you leave the development build.
        </Text>
      </View>

      <View className="flex-row gap-2">
        <Button
          className="flex-1"
          disabled={!hydrated || enabledCount === RELEASE_FEATURE_FLAG_MANIFEST.length}
          onPress={() => onSetAll(true)}
          size="sm"
          variant="outline"
        >
          <Text>Enable all</Text>
        </Button>
        <Button
          className="flex-1"
          disabled={!hydrated || enabledCount === 0}
          onPress={() => onSetAll(false)}
          size="sm"
          variant="outline"
        >
          <Text>Disable all</Text>
        </Button>
      </View>

      <View className="overflow-hidden rounded-xl border border-border">
        {RELEASE_FEATURE_FLAG_MANIFEST.map(([feature, label], index) => (
          <View
            className={
              index === 0
                ? 'min-h-16 flex-row items-center gap-3 p-4'
                : 'min-h-16 flex-row items-center gap-3 border-t border-border p-4'
            }
            key={feature}
          >
            <View className="min-w-0 flex-1 gap-1">
              <Text className="font-sans font-bold">{label}</Text>
              <Text className="text-xs text-muted-foreground" variant="mono">
                {RELEASE_FEATURE_FLAGS[feature]}
              </Text>
            </View>
            <Host
              accessibilityLabel={label}
              accessibilityRole="switch"
              accessibilityState={{ checked: values[feature], disabled: !hydrated }}
              matchContents
              seedColor={BRAND.color.orange}
              style={{ minHeight: 44, width: 64 }}
            >
              <Switch
                disabled={!hydrated}
                onValueChange={(enabled) => onSetFeature(feature, enabled)}
                testID={`dev-release-feature-${feature}`}
                value={values[feature]}
              />
            </Host>
          </View>
        ))}
      </View>
    </View>
  );
}

export function DevTimeController({
  store = devClockStore,
  featureStore = devReleaseFeatureStore,
}: Readonly<{
  store?: DevClockStore;
  featureStore?: DevReleaseFeatureStore;
}>) {
  const pathname = usePathname();
  const [open, setOpen] = React.useState(false);
  const state = useClockState(store);
  const featureState = useDevReleaseFeatureState(featureStore);
  const now = useCurrentTime(store);
  const insets = useSafeAreaInsets();
  const { height } = useWindowDimensions();
  const [dragOffset] = React.useState(() => new Animated.ValueXY());
  const [panResponder] = React.useState(() =>
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, gesture) =>
        Math.abs(gesture.dx) > 4 || Math.abs(gesture.dy) > 4,
      onPanResponderGrant: () => {
        dragOffset.extractOffset();
      },
      onPanResponderMove: (_, gesture) => {
        dragOffset.setValue({ x: gesture.dx, y: gesture.dy });
      },
      onPanResponderRelease: () => {
        dragOffset.flattenOffset();
      },
      onPanResponderTerminate: () => {
        dragOffset.flattenOffset();
      },
    })
  );

  if (!__DEV__ || pathname === '/route-preview') return null;

  const pillLabel = state.enabled
    ? `${now.toLocaleDateString([], { day: '2-digit', month: 'short' })} ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · ${state.speed}×`
    : 'LIVE · 1×';
  const enabledFeatureCount = RELEASE_FEATURE_FLAG_MANIFEST.filter(
    ([feature]) => featureState.values[feature]
  ).length;
  const toolLabel = `${pillLabel} · ${enabledFeatureCount} FLAGS`;

  return (
    <>
      <Animated.View
        {...panResponder.panHandlers}
        style={{
          bottom: Math.max(insets.bottom, 12) + 76,
          position: 'absolute',
          right: 12,
          transform: dragOffset.getTranslateTransform(),
          zIndex: 50,
        }}
      >
        <Pressable
          accessibilityLabel={`Open development tools, ${toolLabel}`}
          accessibilityHint="Drag to reposition"
          accessibilityRole="button"
          className="min-h-11 justify-center rounded-full border border-white/20 bg-neutral-900 px-3 active:opacity-70"
          onPress={() => setOpen(true)}
        >
          <Text className="font-mono text-xs font-bold text-white">{toolLabel}</Text>
        </Pressable>
      </Animated.View>

      <Modal animationType="slide" onRequestClose={() => setOpen(false)} transparent visible={open}>
        <Pressable className="flex-1 justify-end bg-black/50" onPress={() => setOpen(false)}>
          <Pressable onPress={(event) => event.stopPropagation()}>
            <Card
              className="rounded-b-none rounded-t-3xl border-b-0"
              style={{ paddingBottom: Math.max(insets.bottom, 16) }}
            >
              <CardHeader className="flex-row items-start justify-between gap-4">
                <View className="min-w-0 flex-1 gap-1">
                  <CardTitle>Development tools</CardTitle>
                  <CardDescription>
                    Simulate time and choose the release features visible on this device.
                  </CardDescription>
                </View>
                <Button
                  accessibilityLabel="Close development tools"
                  onPress={() => setOpen(false)}
                  size="icon"
                  variant="ghost"
                >
                  <Icon as={XIcon} className="size-5 text-foreground" />
                </Button>
              </CardHeader>
              <CardContent className="px-0">
                <ScrollView
                  contentContainerStyle={{ gap: 32, paddingBottom: 8, paddingHorizontal: 20 }}
                  keyboardShouldPersistTaps="handled"
                  style={{ maxHeight: height * 0.72 }}
                >
                  <View className="gap-4">
                    <View className="gap-1">
                      <Text variant="h3">Time simulation</Text>
                      <Text variant="muted">
                        Jump through event states without changing device time.
                      </Text>
                    </View>
                    <DevTimeControls
                      now={now}
                      onJumpBy={(duration) => store.jumpBy(duration)}
                      onReset={() => store.reset()}
                      onSetSpeed={(speed) => store.setSpeed(speed)}
                      onSetTime={(time) => store.setTime(time)}
                      speed={state.speed}
                    />
                  </View>
                  <DevReleaseFeatureControls
                    hydrated={featureState.hydrated}
                    onSetAll={(enabled) => featureStore.setAll(enabled)}
                    onSetFeature={(feature, enabled) => featureStore.setFeature(feature, enabled)}
                    values={featureState.values}
                  />
                </ScrollView>
              </CardContent>
            </Card>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}
